<script setup lang="ts">
  import { ref, defineProps, defineEmits } from 'vue'
  import { XIcon } from 'lucide-vue-next'
  import { HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/vue/24/outline'
  import { FeedbackService } from '@/types/generated/services.gen'

  const props = defineProps({
    id: {
      type: String,
      required: true
    }
  })

  const emit = defineEmits(['close', 'submitted'])

  const rating = ref<'positive' | 'negative' | null>(null)
  const feedbackText = ref<string>('')
  const isSubmitting = ref(false)

  const submitFeedback = async () => {
    if (rating.value === null) return

    isSubmitting.value = true
    try {
      await FeedbackService.feedbackCreate({
        requestBody: {
          rating: rating.value,
          feedback_type: 'DOCUMENT_GENERATOR',
          related_uuid: props.id,
          url: window.location.href,
          feature: 'DOCUMENT_GENERATOR',
          comment: feedbackText.value || ''
        }
      })

      emit('submitted', {
        isPositive: rating.value === 'positive',
        feedback: feedbackText.value
      })
    } catch (error) {
      console.error('Error submitting feedback:', error)
    } finally {
      isSubmitting.value = false
    }
  }

  const handleRating = (value: 'positive' | 'negative') => {
    rating.value = value
  }

  const handleClose = () => {
    emit('close')
  }
</script>

<template>
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black bg-opacity-25 transition-opacity"></div>

    <div class="flex min-h-full items-center justify-center p-4 text-center">
      <!-- Modal panel -->
      <div
        class="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-xl
          transition-all"
      >
        <!-- Close button -->
        <button
          @click="handleClose"
          class="absolute right-4 top-4 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
        >
          <XIcon class="h-5 w-5" />
        </button>

        <!-- Content -->
        <div class="text-center">
          <h3 class="text-xl font-semibold text-gray-900">Ihr Dokument wurde heruntergeladen</h3>
          <p class="mt-2 text-gray-600">Wie war Ihre Erfahrung mit dem Dokumentengenerator?</p>

          <!-- Thumbs Rating -->
          <div class="mt-6 flex items-center justify-center gap-4">
            <button
              @click="handleRating('positive')"
              :class="[
                rating === 'positive'
                  ? 'bg-purple-100 text-purple-600'
                  : 'bg-gray-100 text-gray-400',
                'rounded-full p-3 duration-200 hover:bg-purple-50 hover:text-purple-600'
              ]"
            >
              <HandThumbUpIcon class="h-6 w-6" />
            </button>
            <button
              @click="handleRating('negative')"
              :class="[
                rating === 'negative'
                  ? 'bg-purple-100 text-purple-600'
                  : 'bg-gray-100 text-gray-400',
                'rounded-full p-3 duration-200 hover:bg-purple-50 hover:text-purple-600'
              ]"
            >
              <HandThumbDownIcon class="h-6 w-6" />
            </button>
          </div>

          <!-- Feedback textarea -->
          <div class="mt-6">
            <textarea
              v-model="feedbackText"
              rows="3"
              class="w-full resize-none rounded-md border border-gray-300 p-3 text-sm shadow-sm placeholder:text-gray-400
                focus:border-purple-500 focus:ring-purple-500"
              placeholder="Optional: Teilen Sie uns Ihr detailliertes Feedback mit..."
            ></textarea>
          </div>

          <!-- Action buttons -->
          <div class="mt-6 flex gap-3">
            <button
              @click="handleClose"
              class="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700
                shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Später
            </button>
            <button
              @click="submitFeedback"
              :disabled="rating === null || isSubmitting"
              class="flex-1 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm
                hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span v-if="!isSubmitting">Feedback senden</span>
              <span
                v-else
                class="flex items-center justify-center"
              >
                <svg
                  class="h-5 w-5 animate-spin"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                    fill="none"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
